import { z } from 'zod'
import { localeSchema, optionalText, pageQuerySchema, versionSchema } from './common.js'

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .nullish()
  .transform((v) => (v ? v : null))
  .pipe(z.email().nullable())

/** Phone numbers: digits, spaces, +, -, parentheses. Stored trimmed; uniqueness compares digits only. */
const optionalPhone = optionalText(30).pipe(
  z
    .string()
    .regex(/^\+?[\d\s()-]{3,30}$/, 'Enter a valid phone number')
    .nullable(),
)

const requiredPhone = z
  .string()
  .trim()
  .max(30)
  .regex(/^\+?[\d\s()-]{3,30}$/, 'Enter a valid phone number')

export const supplierStatusSchema = z.enum(['ACTIVE', 'INACTIVE'])
export type SupplierStatus = z.infer<typeof supplierStatusSchema>

export const supplierInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  nameAr: optionalText(200),
  company: optionalText(200),
  contactPerson: optionalText(200),
  email: optionalEmail,
  mobile: requiredPhone,
  phone: optionalPhone,
  taxNumber: optionalText(50),
  address: optionalText(500),
  city: optionalText(100),
  country: z.string().trim().max(100).default('Oman'),
  notes: optionalText(2000),
  bankName: optionalText(100),
  bankAccountName: optionalText(200),
  bankAccountNumber: optionalText(50),
  bankIban: optionalText(34),
  bankSwift: optionalText(11),
  status: supplierStatusSchema.default('ACTIVE'),
  groupIds: z.array(z.uuid()).max(50).default([]),
})
export type SupplierInput = z.input<typeof supplierInputSchema>

/** Full replacement of the editable fields; `version` guards against overwriting someone else's edit. */
export const supplierUpdateSchema = supplierInputSchema.extend({ version: versionSchema })
export type SupplierUpdate = z.input<typeof supplierUpdateSchema>

/**
 * Balance filter of the supplier list (net balance = the payables aging total, as of today in Muscat;
 * credit-positive: positive is what we owe the supplier):
 *   any      no filter (default)
 *   owes     balance above zero: we owe the supplier
 *   overdue  a purchase balance past its due date (`overdue` above zero)
 *   credit   balance below zero: our credit with the supplier (advances, unapplied returns) exceeds
 *            what we owe
 * Any value other than `any`, and `sort=balance`, need `purchasing.read` (403 otherwise).
 */
export const supplierBalanceFilterSchema = z.enum(['any', 'owes', 'overdue', 'credit'])
export type SupplierBalanceFilter = z.infer<typeof supplierBalanceFilterSchema>

export const supplierListQuerySchema = pageQuerySchema.extend({
  /** Default: everything not archived. */
  view: z.enum(['current', 'active', 'inactive', 'archived']).default('current'),
  groupId: z.uuid().optional(),
  balance: supplierBalanceFilterSchema.default('any'),
})
export type SupplierListQuery = z.input<typeof supplierListQuerySchema>

/** `balance` sorts by the net balance and needs `purchasing.read`. Default: `code`. */
export const SUPPLIER_SORT_FIELDS = [
  'code',
  'name',
  'company',
  'email',
  'city',
  'createdAt',
  'balance',
] as const

/** The most rows one export returns; a larger filtered set is refused (422 `EXPORT_TOO_LARGE`). */
export const SUPPLIER_EXPORT_MAX_ROWS = 50_000

/**
 * `GET /suppliers/export` (suppliers.export): the list's filters and sort, every matching row (no
 * paging), as a CSV file (UTF-8 with a byte-order mark). `lang` picks the header and status labels.
 * Balance columns only with `purchasing.read`. Identity and contact columns only: bank details stay
 * on the profile.
 */
export const supplierExportQuerySchema = supplierListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type SupplierExportQuery = z.input<typeof supplierExportQuerySchema>

/**
 * `GET /suppliers/options`: the supplier picker of purchasing forms, for users who record purchases,
 * payments, returns or refunds without browsing suppliers. Archived suppliers are never offered.
 */
export const supplierOptionsQuerySchema = z.object({
  /** Code, name (English or Arabic), company, or at least 3 digits of the mobile number. */
  q: z.string().trim().max(200).optional(),
  /** One record by id, whatever its status (a form opened with a preset shows its name). */
  id: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** `active` (default): what purchases take. `current`: inactive suppliers too (list filters). */
  view: z.enum(['active', 'current']).default('active'),
})
export type SupplierOptionsQuery = z.input<typeof supplierOptionsQuerySchema>

/** What a picker needs: never balances, bank details, contact details or notes. */
export interface SupplierOption {
  id: string
  code: string
  name: string
  nameAr: string | null
  status: SupplierStatus
  /** Printed on purchase and return documents. */
  taxNumber: string | null
}

export interface SupplierGroupRef {
  id: string
  name: string
  nameAr: string | null
}

export interface SupplierListItem {
  id: string
  code: string
  name: string
  nameAr: string | null
  company: string | null
  mobile: string | null
  email: string | null
  city: string | null
  status: SupplierStatus
  archived: boolean
  groups: SupplierGroupRef[]
  createdAt: string
  /**
   * Present only when the caller holds `purchasing.read` (omitted otherwise). As of today
   * (Asia/Muscat): the payables aging total, which equals the statement closing balance. Open purchase
   * balances minus unallocated credit; negative when we hold credit with the supplier.
   */
  balance?: string
  /** Open purchase balances past their due date (before credit). With `purchasing.read` only. */
  overdue?: string
  /** Credit held on posted payments and returns (not allocated, not refunded). With `purchasing.read` only. */
  unallocatedCredit?: string
}

export interface Supplier {
  id: string
  code: string
  name: string
  nameAr: string | null
  company: string | null
  contactPerson: string | null
  email: string | null
  mobile: string | null
  phone: string | null
  taxNumber: string | null
  address: string | null
  city: string | null
  country: string
  notes: string | null
  bankName: string | null
  bankAccountName: string | null
  bankAccountNumber: string | null
  bankIban: string | null
  bankSwift: string | null
  status: SupplierStatus
  archivedAt: string | null
  groups: SupplierGroupRef[]
  version: number
  createdAt: string
  updatedAt: string
}

/** Counts for the list's view tabs. */
export interface SupplierSummary {
  current: number
  active: number
  inactive: number
  archived: number
}

export const supplierGroupInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  description: optionalText(500),
})
export type SupplierGroupInput = z.input<typeof supplierGroupInputSchema>

/** `version` guards against overwriting someone else's edit (409 VERSION_CONFLICT). */
export const supplierGroupUpdateSchema = supplierGroupInputSchema.extend({ version: versionSchema })
export type SupplierGroupUpdate = z.input<typeof supplierGroupUpdateSchema>

export interface SupplierGroup extends SupplierGroupRef {
  description: string | null
  memberCount: number
  version: number
}
